import { useState } from "react";
import { toast } from "sonner";
import { Info, LockSimple, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import RegionDialog from "@/components/admin/RegionDialog";
import StatusControl from "@/components/admin/StatusControl";
import EligibilityTester from "@/components/admin/EligibilityTester";
import ZipImport from "@/components/admin/ZipImport";
import RegionHistory from "@/components/admin/RegionHistory";
import { api } from "@/lib/api";
import { PRECEDENCE_HELP, REGION_TYPES, SANCTIONED_COUNTRIES, SANCTIONED_SUBREGIONS, isSanctioned } from "@/lib/constants";
import { countryName } from "@/lib/countries";
import { formatDate } from "@/lib/format";

const typeLabel = (t) => REGION_TYPES.find((x) => x.key === t)?.label || t;

function RegionRow({ r, onStatus, onEdit, onDelete }) {
  const locked = isSanctioned(r);
  const shown = r.values.slice(0, 8).join(", ");
  return (
    <li className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-center" data-testid={`region-row-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{r.label || `${typeLabel(r.type)} region`}</p>
          <Pill>{typeLabel(r.type)}</Pill>
          {locked && <Pill tone="inverse" data-testid={`region-locked-${r.id}`}><LockSimple size={12} />Hard-blocked</Pill>}
        </div>
        <p className="cw-soft mt-1 truncate text-sm" title={r.values.join(", ")}>{shown}{r.values.length > 8 ? ` +${r.values.length - 8} more` : ""}</p>
        <p className="cw-muted mt-1 text-xs" data-testid={`region-place-${r.id}`}>{[r.state, r.country && countryName(r.country)].filter(Boolean).join(" · ") || (r.type === "country" ? "" : "No country")} · updated {formatDate(r.updated_at)} by {r.updated_by}</p>
      </div>
      <div className="flex items-center gap-2">
        <StatusControl value={r.status} locked={locked} onChange={(s) => onStatus(r, s)} testId={`region-status-${r.id}`} />
        <Button size="icon" variant="ghost" onClick={() => onEdit(r)} aria-label="Edit region" data-testid={`region-edit-button-${r.id}`}><PencilSimple /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(r)} aria-label="Delete region" className="cw-error" data-testid={`region-delete-button-${r.id}`}><Trash /></Button>
      </div>
    </li>
  );
}

function Sanctioned() {
  const { data } = useAdminLoad("/admin/regions/sanctioned");
  const names = data ? [...data.countries.map((c) => `${c.name} (${c.code})`), ...data.subregions.map((w) => w.charAt(0).toUpperCase() + w.slice(1))]
    : [...Object.values(SANCTIONED_COUNTRIES), ...SANCTIONED_SUBREGIONS];
  return (
    <Panel className="bg-[var(--cw-surface-tertiary)] p-6" data-testid="sanctioned-regions">
      <div className="flex items-center gap-2"><LockSimple size={18} aria-hidden /><h2 className="font-semibold">Sanctioned: locked</h2><Pill tone="inverse" className="ml-auto" data-testid="sanctioned-readonly">Read-only</Pill></div>
      <p className="cw-soft mt-1 text-sm">Always Off. They can’t be set to Waitlist or On, can’t join the waitlist, and invite codes don’t work there. This list can’t be edited.</p>
      <ul className="mt-4 flex flex-wrap gap-2">
        {names.map((n) => (
          <li key={n}><Pill tone="neutral" className="py-1" data-testid={`sanctioned-${n.toLowerCase().replace(/\s/g, "-")}`}><LockSimple size={12} />{n}</Pill></li>
        ))}
      </ul>
    </Panel>
  );
}

export default function Eligibility() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/regions");
  const [dialog, setDialog] = useState({ open: false, region: null });
  const [deleting, setDeleting] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const bump = () => setHistoryKey((k) => k + 1);
  const replace = (region) => setData((d) => ({ items: d.items.map((x) => (x.id === region.id ? region : x)) }));

  const onStatus = async (r, status) => {
    try {
      replace((await api(`/admin/regions/${r.id}`, { method: "PATCH", body: { status } })).region);
      bump();
      toast.success(`${r.label || "Region"} is now ${status === "on" ? "On" : status === "off" ? "Off" : "Waitlist"}.`);
    } catch (e) { toast.error(e.message); }
  };
  const onSaved = (region, editing) => {
    if (editing) replace(region); else setData((d) => ({ items: [region, ...(d?.items || [])] }));
    bump();
    toast.success(editing ? "Rule saved." : "Rule added.");
  };
  const del = async () => {
    await api(`/admin/regions/${deleting.id}`, { method: "DELETE" });
    setData((d) => ({ items: d.items.filter((x) => x.id !== deleting.id) }));
    bump();
    toast.success("Region deleted. Those places fall back to the next match.");
  };

  return (
    <div data-testid="admin-eligibility-page">
      <PageHeader title="Eligibility" description="Decide where Crushwink is Off, on the Waitlist, or On, in any country."
        actions={<Button onClick={() => setDialog({ open: true, region: null })} data-testid="region-create-button"><Plus />Add rule</Button>} />
      <p className="cw-soft -mt-2 mb-6 text-sm" data-testid="eligibility-precedence-help">{PRECEDENCE_HELP}</p>
      <div className="cw-tone-warning mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="eligibility-test-zip-note">
        <Info size={18} className="mt-0.5 shrink-0" aria-hidden />
        <p>Remove test ZIP 99999 before launch (see the <Link to="/admin/settings" className="underline">Pre-launch checklist</Link>).</p>
      </div>
      <div className="space-y-6">
        <LoadState loading={loading} error={error} onRetry={reload}>
          <Panel className="overflow-hidden">
            {data?.items?.length ? <ul className="divide-y divide-border" data-testid="regions-list">
              {data.items.map((r) => <RegionRow key={r.id} r={r} onStatus={onStatus} onEdit={(rr) => setDialog({ open: true, region: rr })} onDelete={setDeleting} />)}
            </ul> : <Empty title="No regions yet" text="Everywhere is Off until you add one." testId="regions-empty" />}
          </Panel>
        </LoadState>
        <div className="grid gap-6 lg:grid-cols-2">
          <ZipImport regions={data?.items || []} onImported={(region, merged) => { if (region) { if (merged) replace(region); else setData((d) => ({ items: [region, ...(d?.items || [])] })); } bump(); }} />
          <EligibilityTester />
        </div>
        <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]"><Sanctioned /><RegionHistory refreshKey={historyKey} /></div>
      </div>
      <RegionDialog open={dialog.open} region={dialog.region} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={onSaved} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testId="region-delete-confirm-dialog"
        title={`Delete ${deleting?.label || "this region"}?`} confirmLabel="Delete region" onConfirm={del}
        description="People in these places fall back to a broader match, or Off if there is none." />
    </div>
  );
}
